import React, { useState, useMemo } from "react";
import { DataTable, Badge, Pagination } from "@/components/ui";
import { fmt, fmtDate } from "@/lib/formatters";
import { Eye, Edit2, UserX } from "lucide-react";

export const MaterialExpensesTable = ({ expenses, onView, onEdit, onToggleDisable }) => {
  const [page, setPage] = useState(1);
  const limit = 10;

  const columns = [
    {
      header: "Date",
      accessor: (row) => (
        <div className="text-slate-600 !text-[10px] flex items-center">
          {row.date ? fmtDate(row.date) : "—"}
        </div>
      ),
      minWidth: "90px",
    },
    {
      header: "Quotation No",
      accessor: (row) => (
        <span className="text-[10px] uppercase tracking-tight whitespace-nowrap text-brand-blue font-medium">
          {row.expenseId || row.invoiceNo || "—"}
        </span>
      ),
      minWidth: "110px",
    },
    {
      header: 'Customer Info',
      accessor: (row) => {
        const p = row.project || {};
        const fullName = p.customerName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || row.clientName || 'No Name';
        const phone = p.phoneNumber || p.phone || '—';
        const email = p.email || '';
        const contactStr = email ? `${phone} | ${email}` : phone;
        
        return (
          <div className="flex flex-col min-w-0" title={`${fullName}\n${contactStr}`}>
            <span className="text-brand-dark tracking-tight text-sm truncate" title={fullName}>{fullName}</span>
            <span className="text-[10px] text-slate-400 truncate mt-0.5" title={contactStr}>
              {phone}{email && <span className="mx-1 text-slate-300">|</span>}{email}
            </span>
          </div>
        );
      },
      minWidth: '200px',
    },
    {
      header: 'Design',
      accessor: (row) => (
        <span className="text-[11px] text-slate-600 truncate max-w-[100px]" title={row.supplier}>
          {row.supplier || '—'}
        </span>
      ),
      minWidth: '100px',
    },
    {
      header: 'Colour',
      accessor: (row) => (
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-slate-600">{row.colour || '—'}</span>
        </div>
      ),
      minWidth: '80px',
    },
    {
      header: 'Warranty',
      accessor: (row) => (
        <span className="text-[11px] text-slate-600">{row.warranty || '—'}</span>
      ),
      minWidth: '80px',
    },
    {
      header: "Material Name",
      accessor: (row) => (
        <div className="text-slate-600 !text-[10px] font-medium flex items-center truncate max-w-[130px]" title={row.materialName}>
          {row.materialName || "—"}
        </div>
      ),
      minWidth: "130px",
    },
    {
      header: "Category",
      accessor: (row) => (
        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded text-brand-blue bg-blue-50 border border-blue-100">
          {row.category || "—"}
        </span>
      ),
      minWidth: "110px",
    },
    {
      header: "Quantity",
      accessor: (row) => (
        <div className="text-slate-600 !text-[10px] font-bold flex items-center">
          {row.quantity || "—"}
        </div>
      ),
      minWidth: "80px",
    },
    {
      header: "Amount",
      accessor: (row) => (
        <div className="text-rose-500 !text-[10px] font-bold flex items-center">
          {fmt(row.amount || 0)}
        </div>
      ),
      minWidth: "110px",
    },
    {
      header: "Status",
      accessor: (row) => (
        <Badge status={row.status || "pending"} />
      ),
      minWidth: "100px",
    },
    {
      header: "Actions",
      accessor: (row) => {
        const isCancelled = row.status === 'cancelled';
        return (
          <div className="flex items-center justify-center">
            <button
              onClick={(e) => { e.stopPropagation(); onView && onView(row); }}
              className="p-1.5 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
              title="View Detail"
            >
              <Eye size={16} />
            </button>
          </div>
        );
      },
      minWidth: "60px",
    }
  ];

  const getRowClassName = (row) => {
    return row.status === 'cancelled' ? "opacity-50 grayscale saturate-0" : "";
  };

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return expenses.slice(startIndex, endIndex);
  }, [expenses, page, limit]);

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-sm border border-slate-200">
      <div className="flex-1 min-h-[400px]">
        <DataTable
          columns={columns}
          data={paginatedData}
          onRowClick={(row) => onView && onView(row)}
          getRowClassName={getRowClassName}
          headerRowStyle={{ backgroundColor: '#475569', color: '#fff' }}
          headerCellStyle={{ color: '#fff' }}
        />
      </div>
      
      {expenses.length > 0 && (
        <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50 rounded-b-lg">
          <div className="text-sm text-slate-500 font-medium">
            Showing <span className="text-slate-700 font-bold">{paginatedData.length}</span> of <span className="text-slate-700 font-bold">{expenses.length}</span> entries
          </div>
          <Pagination
            page={page}
            totalPages={Math.ceil(expenses.length / limit)}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
};
